import React, { useState, useEffect } from 'react';
import { X, Car, Info } from 'lucide-react';
import { useVehicleStore, vehicleUtils } from '@/stores/vehicleStore';
import { formatCostPerMile, formatVehicleType } from '@/utils/formatters';
import type { VehicleConfig } from '@/types';

interface VehicleConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const VehicleConfigModal: React.FC<VehicleConfigModalProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    vehicleConfig,
    isCustomConfig,
    setVehicleType,
    setCostPerMile,
    resetToDefault,
  } = useVehicleStore();

  const [localConfig, setLocalConfig] = useState<VehicleConfig>(vehicleConfig);
  const [activeTab, setActiveTab] = useState<'preset' | 'custom'>('preset');
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    setLocalConfig(vehicleConfig);
    setActiveTab(isCustomConfig ? 'custom' : 'preset');
  }, [vehicleConfig, isCustomConfig]);

  const handlePresetSelect = (type: 'petrol' | 'diesel' | 'hybrid' | 'electric') => {
    const defaultConfig = vehicleUtils.getDefaultConfig(type);
    setLocalConfig(defaultConfig);
    setVehicleType(type);
  };

  const handleCustomChange = (field: keyof VehicleConfig, value: any) => {
    const newConfig = { ...localConfig, [field]: value };
    setLocalConfig(newConfig);
    
    const validation = vehicleUtils.validateConfig(newConfig);
    setErrors(validation?.errors || []);
  };

  const handleSaveCustom = () => {
    const validation = vehicleUtils.validateConfig(localConfig);
    
    if (validation?.isValid) {
      setCostPerMile(localConfig.costPerMile);
      onClose();
    } else {
      setErrors(validation?.errors || []);
    }
  };


  if (!isOpen) return null;

  const presetVehicles = [
    {
      type: 'petrol' as const,
      label: 'Petrol Car',
      description: 'Average petrol vehicle',
      icon: '⛽',
      costPerMile: '15p',
      co2: 'Medium',
    },
    {
      type: 'diesel' as const,
      label: 'Diesel Car',
      description: 'Average diesel vehicle',
      icon: '🚗',
      costPerMile: '12p',
      co2: 'Medium',
    },
    {
      type: 'hybrid' as const,
      label: 'Hybrid',
      description: 'Petrol-electric hybrid',
      icon: '🔋',
      costPerMile: '8p',
      co2: 'Low',
    },
    {
      type: 'electric' as const,
      label: 'Electric',
      description: 'Fully electric vehicle',
      icon: '⚡',
      costPerMile: '4p',
      co2: 'Very Low',
    },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-2">
            <Car className="w-6 h-6 text-amber-500" />
            <h2 className="text-xl font-semibold text-gray-900 font-brand">Vehicle Configuration</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('preset')}
            className={`flex-1 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'preset'
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Preset Vehicles
          </button>
          <button
            onClick={() => setActiveTab('custom')}
            className={`flex-1 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'custom'
                ? 'border-amber-500 text-amber-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Custom Settings
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'preset' ? (
            <div className="space-y-4">
              <div className="text-sm text-gray-600 mb-4">
                Choose a preset vehicle type with typical fuel efficiency and running costs:
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {presetVehicles.map((vehicle) => (
                  <button
                    key={vehicle.type}
                    onClick={() => handlePresetSelect(vehicle.type)}
                    className={`p-4 border rounded-lg text-left transition-all hover:shadow-md ${
                      vehicleConfig.fuelType === vehicle.type && !isCustomConfig
                        ? 'border-amber-500 bg-amber-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-2xl">{vehicle.icon}</span>
                      <div>
                        <div className="font-medium text-gray-900">{vehicle.label}</div>
                        <div className="text-xs text-gray-500">{vehicle.description}</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 gap-1 text-xs text-gray-600">
                      <div>
                        <span className="font-medium">{vehicle.costPerMile}/mi</span>
                      </div>
                      <div>
                        <span className="text-green-600">CO2: {vehicle.co2}</span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-sm text-gray-600 mb-4">
                Configure your vehicle's specific fuel efficiency and running costs:
              </div>

              {/* Fuel Type */}
              <div>
                <label htmlFor="fuel-type-select" className="block text-sm font-medium text-gray-700 mb-2">
                  Fuel Type
                </label>
                <select
                  id="fuel-type-select"
                  value={localConfig.fuelType}
                  onChange={(e) => handleCustomChange('fuelType', e.target.value)}
                  className="input"
                >
                  <option value="petrol">Petrol</option>
                  <option value="diesel">Diesel</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="electric">Electric</option>
                </select>
              </div>


              {/* Cost per Mile */}
              <div>
                <label htmlFor="cost-per-mile-input" className="block text-sm font-medium text-gray-700 mb-2">
                  Running Cost
                </label>
                <div className="relative">
                  <input
                    id="cost-per-mile-input"
                    type="number"
                    value={localConfig.costPerMile}
                    onChange={(e) => handleCustomChange('costPerMile', parseFloat(e.target.value) || 0)}
                    className="input pr-20"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <span className="absolute right-3 top-2 text-sm text-gray-500">pence/mile</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Total running cost per mile including fuel
                </div>
              </div>

              {/* Current Config Summary */}
              <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Info className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium text-amber-900">Current Configuration</span>
                </div>
                <div className="text-sm text-amber-800">
                  <div>{formatVehicleType(localConfig.fuelType)} • {formatCostPerMile(localConfig.costPerMile)}</div>
                  <div className="mt-1 text-xs">
                    Example: 10-mile round trip would cost {formatCostPerMile(localConfig.costPerMile * 10)}
                  </div>
                </div>
              </div>

              {/* Errors */}
              {errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="text-sm text-red-800">
                    {errors.map((error, index) => (
                      <div key={index}>• {error}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 bg-gray-50 border-t">
          <button
            onClick={() => resetToDefault(vehicleConfig.fuelType)}
            className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            Reset to Default
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            {activeTab === 'custom' && (
              <button
                onClick={handleSaveCustom}
                disabled={errors.length > 0}
                className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Configuration
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleConfigModal;